import { useState, useEffect, useCallback } from 'react';
import API from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import {
  QrCode, UserCheck, MapPin, Loader2, CheckCircle,
  Search, ClipboardCheck
} from 'lucide-react';

const parseServerTime = (s) => new Date(/(?:Z|[+-]\d{2}:?\d{2})$/.test(s) ? s : s + 'Z');

export default function SurveyorDashboard() {
  const [precincts, setPrecincts] = useState([]);
  const [selectedPrecinct, setSelectedPrecinct] = useState(null);
  const [tab, setTab] = useState('all');
  const [voters, setVoters] = useState([]);
  const [loadingPrecincts, setLoadingPrecincts] = useState(true);
  const [loadingVoters, setLoadingVoters] = useState(false);
  const [search, setSearch] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [activePosition, setActivePosition] = useState(null);

  // QR Modal
  const [qrOpen, setQrOpen] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [qrVoter, setQrVoter] = useState(null);

  // Assisted Modal
  const [assistedOpen, setAssistedOpen] = useState(false);
  const [assistedVoter, setAssistedVoter] = useState(null);
  const [assistedForm, setAssistedForm] = useState({ candidate_id: '', response_type: 'candidate', support_confidence: 'solid' });
  const [assistedSubmitting, setAssistedSubmitting] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [p, c, pos] = await Promise.all([
          API.get('/survey/precincts'),
          API.get('/ref/candidates'),
          API.get('/ref/active-position'),
        ]);
        setPrecincts(p.data);
        setCandidates(c.data);
        setActivePosition(pos.data);
        if (p.data.length > 0) setSelectedPrecinct(p.data[0].precinct_id);
      } catch (err) { console.error(err); }
      setLoadingPrecincts(false);
    };
    load();
  }, []);

  const loadVoters = useCallback(async () => {
    if (!selectedPrecinct) return;
    setLoadingVoters(true);
    try {
      const { data } = await API.get(`/survey/voters?precinct_id=${selectedPrecinct}&status=${tab}`);
      setVoters(data);
    } catch (err) { console.error(err); }
    setLoadingVoters(false);
  }, [selectedPrecinct, tab]);

  useEffect(() => { loadVoters(); }, [loadVoters]);

  const handleGenerateQR = async (voter) => {
    setQrVoter(voter);
    setQrOpen(true);
    setQrLoading(true);
    try {
      const { data } = await API.post('/survey/generate-qr', {
        voter_id: voter.id,
        position_id: activePosition?.id,
      });
      setQrData(data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to generate QR');
      setQrOpen(false);
    }
    setQrLoading(false);
  };

  const handleAssistedOpen = (voter) => {
    setAssistedVoter(voter);
    setAssistedForm({ candidate_id: '', response_type: 'candidate', support_confidence: 'solid' });
    setAssistedOpen(true);
  };

  const handleAssistedSubmit = async () => {
    setAssistedSubmitting(true);
    try {
      await API.post('/survey/assisted-submit', {
        voter_id: assistedVoter.id,
        position_id: activePosition?.id,
        candidate_id: assistedForm.response_type === 'candidate' ? parseInt(assistedForm.candidate_id) : null,
        response_type: assistedForm.response_type,
        support_confidence: assistedForm.support_confidence,
      });
      setAssistedOpen(false);
      loadVoters();
    } catch (err) {
      alert(err.response?.data?.detail || 'Submission failed');
    }
    setAssistedSubmitting(false);
  };

  const filtered = voters.filter(v =>
    v.full_name.toLowerCase().includes(search.toLowerCase()) ||
    v.voter_code.toLowerCase().includes(search.toLowerCase())
  );

  const selectedPrecinctData = precincts.find(p => p.precinct_id === selectedPrecinct);

  if (loadingPrecincts) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  }

  return (
    <div data-testid="surveyor-dashboard">
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight">Field Survey</h1>
        <p className="text-slate-500 text-sm mt-1">
          {activePosition?.name || 'No active position'} &middot; {precincts.length} assigned precincts
        </p>
      </div>

      {/* Precinct selector */}
      <div className="flex flex-wrap gap-2 mb-4">
        {precincts.map(p => (
          <button
            key={p.precinct_id}
            data-testid={`precinct-btn-${p.precinct_id}`}
            onClick={() => setSelectedPrecinct(p.precinct_id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all ${
              selectedPrecinct === p.precinct_id
                ? 'bg-slate-900 text-white border-slate-900'
                : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
            }`}
          >
            <MapPin className="w-3.5 h-3.5 inline mr-1" />
            {p.precinct_number}
            <span className="ml-1 opacity-60">({p.surveyed}/{p.total_voters})</span>
          </button>
        ))}
      </div>

      {selectedPrecinctData && (
        <div className="text-xs text-slate-500 mb-4">
          {selectedPrecinctData.barangay_name} &middot; Precinct {selectedPrecinctData.precinct_number} &middot;
          {selectedPrecinctData.completion_pct}% complete
        </div>
      )}

      {/* Search */}
      <div className="relative mb-4">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          data-testid="voter-search-input"
          placeholder="Search voters by name or code..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="pl-9 bg-white"
        />
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4">
          <TabsTrigger data-testid="tab-all" value="all" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white">
            All
          </TabsTrigger>
          <TabsTrigger data-testid="tab-pending" value="pending" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white">
            Pending
          </TabsTrigger>
          <TabsTrigger data-testid="tab-completed" value="completed" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white">
            Completed
          </TabsTrigger>
        </TabsList>

        {/* Unified voter list for all tabs */}
        {['all', 'pending', 'completed'].map(tabKey => (
          <TabsContent key={tabKey} value={tabKey}>
            {loadingVoters ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
            ) : filtered.length === 0 ? (
              <Card className="text-center py-12 border-slate-200">
                <CardContent>
                  {tabKey === 'pending' ? (
                    <>
                      <CheckCircle className="w-12 h-12 text-emerald-300 mx-auto mb-3" />
                      <p className="text-slate-500">All voters surveyed!</p>
                    </>
                  ) : (
                    <p className="text-slate-500">No voters found</p>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {filtered.map((v, idx) => {
                  const isSurveyed = !!v.response;
                  return (
                    <Card key={v.id} className={`border-slate-200 animate-fade-slide-up ${isSurveyed ? 'bg-slate-50/60' : ''}`} style={{ animationDelay: `${idx * 20}ms` }}>
                      <CardContent className="py-3 px-4 flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium ${isSurveyed ? 'text-slate-400 line-through' : 'text-slate-900'}`}>
                            {v.full_name}
                          </p>
                          <p className={`text-xs ${isSurveyed ? 'text-slate-300' : 'text-slate-500'}`}>
                            {v.voter_code} {v.purok || v.address ? `\u00B7 ${v.purok || v.address}` : ''}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {isSurveyed ? (
                            <>
                              <Badge
                                style={{ backgroundColor: v.response.party_color || '#94a3b8', color: '#fff' }}
                                className="text-xs"
                              >
                                {v.response.response_type === 'candidate' ? v.response.candidate_name : v.response.response_type}
                              </Badge>
                              <Badge className={`text-xs ${v.response.support_confidence === 'solid' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                                {v.response.support_confidence}
                              </Badge>
                              <Badge variant="outline" className="text-xs text-slate-400">
                                {v.response.submission_mode === 'self_scan' ? 'QR' : 'Assisted'}
                              </Badge>
                            </>
                          ) : (
                            <>
                              <Button
                                data-testid={`qr-btn-${v.id}`}
                                size="sm"
                                variant="outline"
                                onClick={() => handleGenerateQR(v)}
                                className="text-blue-600 border-blue-200 hover:bg-blue-50"
                              >
                                <QrCode className="w-4 h-4 mr-1" /> QR
                              </Button>
                              <Button
                                data-testid={`assisted-btn-${v.id}`}
                                size="sm"
                                variant="outline"
                                onClick={() => handleAssistedOpen(v)}
                                className="text-slate-600 border-slate-200 hover:bg-slate-50"
                              >
                                <ClipboardCheck className="w-4 h-4 mr-1" /> Assisted
                              </Button>
                            </>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* QR Modal */}
      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent className="max-w-sm text-center">
          <DialogHeader>
            <DialogTitle className="font-heading">Scan to Vote</DialogTitle>
          </DialogHeader>
          {qrLoading ? (
            <div className="py-8"><Loader2 className="w-8 h-8 animate-spin text-slate-400 mx-auto" /></div>
          ) : qrData ? (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">{qrVoter?.full_name}</p>
              <img
                data-testid="qr-code-image"
                src={`data:image/png;base64,${qrData.qr_image}`}
                alt="QR Code"
                className="mx-auto w-48 h-48 rounded-lg border border-slate-200"
              />
              <p className="text-xs text-slate-500 font-mono">
                Expires: {parseServerTime(qrData.expires_at).toLocaleTimeString()}
              </p>
              <Button
                data-testid="regenerate-qr-btn"
                variant="outline"
                onClick={() => handleGenerateQR(qrVoter)}
                className="w-full"
              >
                Regenerate QR
              </Button>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Assisted Submit Modal */}
      <Dialog open={assistedOpen} onOpenChange={setAssistedOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Assisted Submission</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">Recording for: <strong>{assistedVoter?.full_name}</strong></p>

            <div>
              <Label>Response Type</Label>
              <Select value={assistedForm.response_type} onValueChange={v => setAssistedForm({...assistedForm, response_type: v, candidate_id: ''})}>
                <SelectTrigger data-testid="assisted-response-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="candidate">Candidate</SelectItem>
                  <SelectItem value="abstain">Abstain</SelectItem>
                  <SelectItem value="undecided">Undecided</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {assistedForm.response_type === 'candidate' && (
              <div>
                <Label>Select Candidate</Label>
                <div className="space-y-2 mt-2">
                  {candidates.map(c => (
                    <button
                      key={c.id}
                      data-testid={`assisted-candidate-${c.id}`}
                      onClick={() => setAssistedForm({...assistedForm, candidate_id: String(c.id)})}
                      className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                        assistedForm.candidate_id === String(c.id)
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: c.party_color || '#94a3b8' }} />
                        <span className="font-medium text-sm text-slate-900">{c.full_name}</span>
                        <span className="text-xs text-slate-500">{c.party_name}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div>
              <Label>Support Confidence</Label>
              <div className="flex gap-2 mt-2">
                <Button
                  data-testid="confidence-solid"
                  variant={assistedForm.support_confidence === 'solid' ? 'default' : 'outline'}
                  className={`flex-1 ${assistedForm.support_confidence === 'solid' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
                  onClick={() => setAssistedForm({...assistedForm, support_confidence: 'solid'})}
                >
                  Solid
                </Button>
                <Button
                  data-testid="confidence-soft"
                  variant={assistedForm.support_confidence === 'soft' ? 'default' : 'outline'}
                  className={`flex-1 ${assistedForm.support_confidence === 'soft' ? 'bg-amber-600 hover:bg-amber-700' : ''}`}
                  onClick={() => setAssistedForm({...assistedForm, support_confidence: 'soft'})}
                >
                  Soft
                </Button>
              </div>
            </div>

            <Button
              data-testid="assisted-submit-btn"
              onClick={handleAssistedSubmit}
              disabled={assistedSubmitting || (assistedForm.response_type === 'candidate' && !assistedForm.candidate_id)}
              className="w-full bg-slate-900 hover:bg-slate-800"
            >
              {assistedSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserCheck className="w-4 h-4 mr-2" />}
              Submit Response
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
